import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";

interface DraftBookingDialogProps {
  open: boolean;
  draftBookingLink: string;
  onClose: () => void;
}

export default function DraftBookingDialog({
  open,
  draftBookingLink,
  onClose,
}: DraftBookingDialogProps) {
  const { i18n } = useLingui();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draftBookingLink);
      enqueueSnackbar(i18n._(msg`Link copied to clipboard`), { variant: "success" });
    } catch {
      enqueueSnackbar(i18n._(msg`Could not copy link`), { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Trans>Booking saved as draft</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            <Trans>
              Your booking has been saved. Use the link below to resume and complete it later.
            </Trans>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Trans>
              Do not share this link with anyone — it gives access to your booking data.
            </Trans>
          </Typography>
          <TextField
            value={draftBookingLink}
            size="small"
            variant="filled"
            fullWidth
            slotProps={{
              htmlInput: { readOnly: true },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end" aria-label={i18n._(msg`Copy link`)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 0, md: 3 },
          pt: 0,
        }}
      >
        <Button onClick={onClose} variant="contained">
          <Trans>Continue booking</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
